import { ShopClient } from "../../api/ShopClientsAPI";
import InputShortText from "../InputShortText/InputShortText";
import TextArea from "../TextArea/TextArea";
import { useTranslation } from "react-i18next";
import React from "react";
import { isValidEmail, isValidPhone, isValidText } from "../../shared/validation";
import { useFormValidation } from "../../hooks/useFormValidation";
import SelectInput from "../SelectInput/SelectInput";
import Divider from "../Divider/Divider";
import CONFIG from "../../config/config";

type Props = {
  partner: ShopClient,
  onPartnerChange?: (value: ShopClient) => void,
  isDisabled?: boolean,
  actionButton?: JSX.Element
};

const getInitialState = (partner?: ShopClient) => {
  if (partner) {
    return {
      name: !!partner.name && isValidText(partner.name, 2),
      email: !!partner.email && isValidEmail(partner.email),
    };
  }
};

function PartnerForm({ partner, onPartnerChange = () => null, isDisabled = false, actionButton }: Props) {

  const { t } = useTranslation();
  const requiredFields = ['name'];
  const initialState = getInitialState(partner);
  const { updateFieldValidation, isFormValid } = useFormValidation(requiredFields, initialState);

  const disabledActionButton = actionButton
    ? React.cloneElement(
        actionButton,
        {},
        React.Children.map(actionButton.props.children, (child) => {
          return child ? React.cloneElement(child, { isDisabled: !isFormValid() }) : null;
        })
      )
    : null;

  return ( 
    <div className="flex flex-col flex-wrap space-y-6">
      {/* Basic Information Section */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold">{t("admin.form.general_data")}</h3>
        <InputShortText 
          label={"admin.form.name"}
          validationFn={(value) => !!value && isValidText(value, 2)} 
          onValidationChange={(isValid) => updateFieldValidation('name', isValid)}
          onChange={e => onPartnerChange({ ...partner, name: e.currentTarget.value } as ShopClient)}
          errorMessage={"errors.too_short"}
          defaultValue={partner?.name || ''}
          isDisabled={isDisabled}
          helperText={t("admin.form.name.helper")!}
        />
        <InputShortText 
          label={"admin.form.email"} 
          validationFn={(value) => !!value && isValidEmail(value)} 
          onChange={e => onPartnerChange({ ...partner, email: e.currentTarget.value } as ShopClient)}
          errorMessage={"errors.too_short"}
          defaultValue={partner?.email || ''}
          isDisabled={isDisabled}
          helperText={t("admin.form.email.helper")!}
        />
        <div className="flex flex-wrap space-x-5">
          <InputShortText 
            label={"admin.form.phone"} 
            validationFn={(value) => !!value && isValidPhone(value)} 
            onChange={e => onPartnerChange({ ...partner, phone: e.currentTarget.value } as ShopClient)}
            errorMessage={"errors.too_short"}
            defaultValue={partner?.phone || ''}
            isDisabled={isDisabled}
            helperText={t("admin.form.phone.helper")!}
          />
          <SelectInput
            values={CONFIG.COUNTRY_CODES.map(e => { return { value: e.countryCode, readableValue: t(e.countryName?.toLowerCase()) } })}
            onChange={e => onPartnerChange({ ...partner, countryCode: e.currentTarget.value } as ShopClient)}
            label={"admin.form.country_code"}
            defaultValue={partner?.countryCode}
            isDisabled={isDisabled}
          />
        </div>
      </div>

      <Divider />

      {/* Delivery Address Section */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold">{t("admin.form.delivery_data")}</h3>
        <InputShortText 
          label={"admin.form.delivery_receiver"} 
          validationFn={(value) => !!value && isValidText(value, 5)} 
          onValidationChange={(isValid) => updateFieldValidation('addressDataName', isValid)}
          onChange={e => onPartnerChange({ ...partner, addressDataName: e.currentTarget.value } as ShopClient)}
          errorMessage={"errors.too_short"}
          defaultValue={partner?.addressDataName || ''}
          placeholder={"admin.form.delivery_receiver.placeholder"}
          isDisabled={isDisabled}
          helperText={t("admin.form.delivery_receiver.helper")!}
        />
        <InputShortText 
          label={"admin.form.address"} 
          validationFn={(value) => !!value && isValidText(value, 7)} 
          onValidationChange={(isValid) => updateFieldValidation('addressDataAddress', isValid)}
          onChange={e => onPartnerChange({ ...partner, addressDataAddress: e.currentTarget.value } as ShopClient)}
          errorMessage={"errors.too_short"}
          defaultValue={partner?.addressDataAddress || ''}
          placeholder={"admin.form.address.placeholder"}
          isDisabled={isDisabled}
          helperText={t("admin.form.address.helper")!}
        />
        <div className="flex flex-wrap space-x-5">
          <InputShortText 
            label={"admin.form.city"} 
            validationFn={(value) => !!value && isValidText(value, 2, 50)} 
            onChange={e => onPartnerChange({ ...partner, addressDataCity: e.currentTarget.value } as ShopClient)}
            errorMessage={"errors.too_short"}
            defaultValue={partner?.addressDataCity || ''}
            placeholder={"admin.form.city.placeholder"}
            isDisabled={isDisabled}
            helperText={t("admin.form.city.helper")!}
          />
          <InputShortText 
            label={"admin.form.postal_code"} 
            validationFn={(value) => !!value && isValidText(value, 2, 15)} 
            onChange={e => onPartnerChange({ ...partner, addressDataPostalCode: e.currentTarget.value } as ShopClient)}
            errorMessage={"errors.too_short"}
            defaultValue={partner?.addressDataPostalCode || ''}
            placeholder={"admin.form.postal_code.placeholder"}
            isDisabled={isDisabled}
            helperText={t("admin.form.postal_code.helper")!}
          />
        </div>
        <InputShortText 
          label={"admin.form.phone_address"} 
          validationFn={(value) => !!value && isValidPhone(value)} 
          onChange={e => onPartnerChange({ ...partner, phoneAddress: e.currentTarget.value } as ShopClient)}
          errorMessage={"errors.too_short"}
          defaultValue={partner?.phoneAddress}
          placeholder={"admin.form.phone_address.placeholder"}
          isDisabled={isDisabled}
          helperText={t("admin.form.phone_address.helper")!}
        />
      </div>

      <Divider />

      {/* Fiscal Address Section */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold">{t("admin.form.fiscal_data")}</h3>
        <InputShortText 
          label={"admin.form.tax_id"} 
          validationFn={(value) => !!value && isValidText(value, 3)} 
          onChange={e => onPartnerChange({ ...partner, fiscalDataNIP: e.currentTarget.value } as ShopClient)}
          onValidationChange={(isValid) => updateFieldValidation('fiscalDataNIP', isValid)}
          errorMessage={"errors.too_short"}
          defaultValue={partner?.fiscalDataNIP || ''}
          placeholder={"admin.form.tax_id.placeholder"}
          isDisabled={isDisabled}
          helperText={t("admin.form.tax_id.helper")!}
        />
        <InputShortText 
          label={"admin.form.tax_company_name"} 
          validationFn={(value) => !!value && isValidText(value, 3)} 
          onChange={e => onPartnerChange({ ...partner, fiscalDataName: e.currentTarget.value } as ShopClient)}
          onValidationChange={(isValid) => updateFieldValidation('fiscalDataName', isValid)}
          errorMessage={"errors.too_short"}
          defaultValue={partner?.fiscalDataName || ''}
          placeholder={"admin.form.tax_company_name.placeholder"}
          isDisabled={isDisabled}
          helperText={t("admin.form.tax_company_name.helper")!}
        />
        <InputShortText 
          label={"admin.form.tax_company_address"} 
          validationFn={(value) => !!value && isValidText(value, 5)} 
          onChange={e => onPartnerChange({ ...partner, fiscalDataAddress: e.currentTarget.value } as ShopClient)}
          onValidationChange={(isValid) => updateFieldValidation('fiscalDataAddress', isValid)}
          errorMessage={"errors.too_short"}
          defaultValue={partner?.fiscalDataAddress || ''}
          placeholder={"admin.form.tax_company_address.placeholder"}
          isDisabled={isDisabled}
          helperText={t("admin.form.tax_company_address.helper")!}
        />
        <div className="flex flex-wrap space-x-5">
          <InputShortText 
            label={"admin.form.tax_city"} 
            validationFn={(value) => !!value && isValidText(value, 2, 50)} 
            onChange={e => onPartnerChange({ ...partner, fiscalDataCity: e.currentTarget.value } as ShopClient)}
            errorMessage={"errors.too_short"}
            defaultValue={partner?.fiscalDataCity || ''}
            placeholder={"admin.form.tax_city.placeholder"}
            isDisabled={isDisabled}
            helperText={t("admin.form.tax_city.helper")!}
          />
          <InputShortText 
            label={"admin.form.tax_postal_code"} 
            validationFn={(value) => !!value && isValidText(value, 2, 15)} 
            onChange={e => onPartnerChange({ ...partner, fiscalDataPostalCode: e.currentTarget.value } as ShopClient)}
            errorMessage={"errors.too_short"}
            defaultValue={partner?.fiscalDataPostalCode || ''}
            placeholder={"admin.form.tax_postal_code.placeholder"}
            isDisabled={isDisabled}
            helperText={t("admin.form.tax_postal_code.helper")!}
          />
        </div>
      </div>

      <Divider />

      {/* Additional Information Section */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold">{t("admin.form.other_data")}</h3>
        <TextArea
          label={"admin.form.notes"} 
          validationFn={(value) => !value || (!!value &&  isValidText(value, 0, 1024))}
          onChange={e => onPartnerChange({ ...partner, notes: e.currentTarget.value } as ShopClient)}
          errorMessage={"errors.too_short"}
          defaultValue={partner?.notes}
          isDisabled={isDisabled}
          helperText={t("admin.form.notes.helper")!}
          maxLength={1024}
        />
      </div>

      { disabledActionButton }
    </div>
  );
}

export default PartnerForm;
